/**
 * Mad Dad Labz Easter Egg Discovery System
 * Privacy-first implementation with localStorage-only tracking
 * No external network requests, no PII collection
 */

class MadDadEasterEggSystem {
  constructor() {
    this.storageKey = 'mad-dad-eggs';
    this.maxEggs = 5;
    this.storageAvailable = this.checkStorageAvailability();
    this.isOptedOut = false;
    this.discoveredEggs = new Set();
    this.listeners = new Map();
    
    this.init();
  }

  init() {
    // Check opt-out status first
    if (this.storageAvailable) {
      this.isOptedOut = localStorage.getItem('mad-dad-eggs-opt-out') === 'true';
    } else {
      this.isOptedOut = true;
    }

    if (this.isOptedOut) {
      return; // Exit early if user opted out
    }

    this.loadDiscoveredEggs();
    this.setupPrivacyControls();
    this.bindEvents();
  }

  /**
   * Load discovered eggs from localStorage
   */
  loadDiscoveredEggs() {
    if (!this.storageAvailable) {
      return;
    }

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.discoveredEggs = new Set(data.eggs || []);
      }
    } catch (error) {
      console.warn('Mad Dad Labz: Could not load Easter egg data');
      this.discoveredEggs = new Set();
    }
  }

  /**
   * Save discovered eggs to localStorage
   */
  saveDiscoveredEggs() {
    if (this.isOptedOut || !this.storageAvailable) return;

    try {
      const data = {
        eggs: Array.from(this.discoveredEggs),
        lastUpdated: Date.now(),
        version: '1.0'
      };
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Mad Dad Labz: Could not save Easter egg data');
    }
  }

  /**
   * Setup privacy controls and opt-out mechanism
   */
  setupPrivacyControls() {
    if (!this.storageAvailable) {
      return;
    }

    // Create privacy notice if not already shown
    if (!localStorage.getItem('mad-dad-eggs-notice-shown')) {
      this.showPrivacyNotice();
    }

    // Add opt-out link to footer if it exists for admins/debuggers only
    this.addOptOutLink();
  }

  /**
   * Show privacy notice for Easter egg system
   */
  showPrivacyNotice() {
    if (!this.storageAvailable) {
      return;
    }

    const notice = document.createElement('div');
    notice.className = 'mad-dad-privacy-notice';
    notice.innerHTML = `
      <div class="privacy-notice-content">
        <p>🧪 Mad Dad Labz uses local storage to remember your discovered Easter eggs. No data is sent to servers.</p>
        <button class="privacy-notice-accept" aria-label="Accept Easter egg tracking">Got it!</button>
        <button class="privacy-notice-decline" aria-label="Decline Easter egg tracking">No thanks</button>
      </div>
    `;

    // Style the notice
    notice.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: var(--color-background, #fff);
      border: 2px solid var(--color-primary, #051c36);
      border-radius: 8px;
      padding: 16px;
      max-width: 300px;
      z-index: 1000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-size: 14px;
    `;

    document.body.appendChild(notice);

    // Handle user choice
    notice.querySelector('.privacy-notice-accept').addEventListener('click', () => {
      localStorage.setItem('mad-dad-eggs-notice-shown', 'true');
      notice.remove();
    });

    notice.querySelector('.privacy-notice-decline').addEventListener('click', () => {
      this.optOut();
      notice.remove();
    });

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (notice.parentNode) {
        localStorage.setItem('mad-dad-eggs-notice-shown', 'true');
        notice.remove();
      }
    }, 10000);
  }

  /**
   * Add opt-out link to footer
   */
  addOptOutLink() {
    if (!this.shouldExposeOptOut()) {
      return;
    }

    const footerEl = document.querySelector('footer, .footer');
    if (footerEl && !footerEl.querySelector('.mad-dad-opt-out')) {
      const optOutLink = document.createElement('button');
      optOutLink.className = 'mad-dad-opt-out';
      optOutLink.textContent = 'Disable Easter Eggs';
      optOutLink.style.cssText = `
        background: none;
        border: none;
        color: inherit;
        text-decoration: underline;
        cursor: pointer;
        font-size: 12px;
        opacity: 0.7;
        margin-top: 8px;
      `;
      
      optOutLink.addEventListener('click', () => this.optOut());
      footerEl.appendChild(optOutLink);
    }
  }

  /**
   * Opt out of Easter egg system
   */
  optOut() {
    this.isOptedOut = true;
    if (this.storageAvailable) {
      localStorage.setItem('mad-dad-eggs-opt-out', 'true');
      localStorage.removeItem(this.storageKey);
    }
    this.discoveredEggs.clear();
    this.unbindEvents();
    
    // Remove opt-out link entirely for shoppers
    const optOutLink = document.querySelector('.mad-dad-opt-out');
    if (optOutLink) {
      if (this.shouldExposeOptOut()) {
        optOutLink.textContent = 'Enable Easter Eggs';
        optOutLink.onclick = () => this.optIn();
      } else {
        optOutLink.remove();
      }
    }
  }

  /**
   * Opt back into Easter egg system
   */
  optIn() {
    this.isOptedOut = false;
    if (this.storageAvailable) {
      localStorage.removeItem('mad-dad-eggs-opt-out');
    }
    this.init();
    
    const optOutLink = document.querySelector('.mad-dad-opt-out');
    if (optOutLink) {
      optOutLink.textContent = 'Disable Easter Eggs';
      optOutLink.onclick = () => this.optOut();
    }
  }

  shouldExposeOptOut() {
    return Boolean(window.Shopify?.designMode || localStorage.getItem('mad-dad-debug') === 'true');
  }

  /**
   * Bind event listeners for Easter egg detection
   */
  bindEvents() {
    if (this.isOptedOut) return;

    // Cleanup existing listeners
    this.unbindEvents();

    // Store bound functions for cleanup
    this.listeners.set('keydown', this.handleKeydown.bind(this));
    this.listeners.set('click', this.handleClick.bind(this));
    this.listeners.set('scroll', this.throttle(this.handleScroll.bind(this), 100));
    
    // Add touch event listeners for mobile
    if ('ontouchstart' in window) {
      this.listeners.set('touchstart', this.handleTouch.bind(this));
      this.listeners.set('touchend', this.handleTouch.bind(this));
      document.addEventListener('touchstart', this.listeners.get('touchstart'), { passive: true });
      document.addEventListener('touchend', this.listeners.get('touchend'), { passive: true });
    }

    // Add event listeners
    document.addEventListener('keydown', this.listeners.get('keydown'));
    document.addEventListener('click', this.listeners.get('click'));
    document.addEventListener('scroll', this.listeners.get('scroll'), { passive: true });
  }

  /**
   * Unbind all event listeners
   */
  unbindEvents() {
    this.listeners.forEach((handler, event) => {
      document.removeEventListener(event, handler);
    });
    this.listeners.clear();
  }

  /**
   * Handle keydown events for Easter egg detection
   */
  handleKeydown(event) {
    // Prevent Easter eggs during checkout or form input
    if (this.isInCheckoutFlow() || this.isInFormInput(event.target)) {
      return;
    }

    // Dispatch to specific Easter egg handlers
    this.dispatchEvent('keydown', { key: event.key, code: event.code });
  }

  /**
   * Handle click events for Easter egg detection
   */
  handleClick(event) {
    if (this.isInCheckoutFlow()) return;

    this.dispatchEvent('click', { 
      target: event.target, 
      x: event.clientX, 
      y: event.clientY,
      isTouchDevice: 'ontouchstart' in window
    });
  }

  /**
   * Handle touch events for mobile Easter egg detection
   */
  handleTouch(event) {
    if (this.isInCheckoutFlow()) return;

    const touch = event.touches[0] || event.changedTouches[0];
    this.dispatchEvent('touch', {
      target: event.target,
      x: touch.clientX,
      y: touch.clientY,
      type: event.type
    });
  }

  /**
   * Handle scroll events for Easter egg detection
   */
  handleScroll(event) {
    if (this.isInCheckoutFlow()) return;

    this.dispatchEvent('scroll', { 
      scrollY: window.scrollY, 
      scrollX: window.scrollX 
    });
  }

  /**
   * Check if user is in checkout flow
   */
  isInCheckoutFlow() {
    const checkoutPaths = ['/cart', '/checkout', '/thank_you', '/orders'];
    return checkoutPaths.some(path => window.location.pathname.includes(path));
  }

  /**
   * Check if target is a form input
   */
  isInFormInput(target) {
    const formElements = ['INPUT', 'TEXTAREA', 'SELECT'];
    return formElements.includes(target.tagName) || target.contentEditable === 'true';
  }

  /**
   * Dispatch event to registered Easter egg handlers
   */
  dispatchEvent(type, data) {
    const event = new CustomEvent(`mad-dad-egg-${type}`, { 
      detail: data,
      bubbles: false,
      cancelable: false
    });
    document.dispatchEvent(event);
  }

  /**
   * Register an Easter egg discovery
   */
  discoverEgg(eggId, reward = null) {
    if (this.isOptedOut || this.discoveredEggs.has(eggId)) {
      return false;
    }

    if (this.discoveredEggs.size >= this.maxEggs) {
      return false; // Max eggs reached
    }

    this.discoveredEggs.add(eggId);
    this.saveDiscoveredEggs();
    this.showEggReward(eggId, reward);
    
    return true;
  }

  /**
   * Show reward for discovered Easter egg
   */
  showEggReward(eggId, reward) {
    const rewardElement = document.createElement('div');
    rewardElement.className = 'mad-dad-egg-reward';
    rewardElement.innerHTML = `
      <div class="egg-reward-content">
        <span class="egg-icon">🥚</span>
        <span class="egg-message">${reward || 'Easter egg discovered!'}</span>
        <span class="egg-progress">${this.discoveredEggs.size}/${this.maxEggs}</span>
      </div>
    `;

    rewardElement.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--color-accent, #52c301);
      color: var(--color-background, #fff);
      padding: 12px 16px;
      border-radius: 8px;
      z-index: 1001;
      animation: eggSlideIn 0.3s ease-out;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    `;

    // Add animation keyframes if not already present
    if (!document.querySelector('#mad-dad-egg-styles')) {
      const style = document.createElement('style');
      style.id = 'mad-dad-egg-styles';
      style.textContent = `
        @keyframes eggSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes eggSlideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(rewardElement);

    // Remove after 3 seconds
    setTimeout(() => {
      rewardElement.style.animation = 'eggSlideOut 0.3s ease-in';
      setTimeout(() => rewardElement.remove(), 300);
    }, 3000);

    return rewardElement;
  }

  /**
   * Get discovery progress
   */
  getProgress() {
    return {
      discovered: this.discoveredEggs.size,
      total: this.maxEggs,
      percentage: (this.discoveredEggs.size / this.maxEggs) * 100,
      isComplete: this.discoveredEggs.size >= this.maxEggs
    };
  }

  /**
   * Check if specific egg is discovered
   */
  isEggDiscovered(eggId) {
    return this.discoveredEggs.has(eggId);
  }

  /**
   * Throttle function for performance
   */
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * Cleanup method for page unload
   */
  destroy() {
    this.unbindEvents();
    
    // Remove privacy notice if present
    const notice = document.querySelector('.mad-dad-privacy-notice');
    if (notice) notice.remove();
  }

  /**
   * Detect if localStorage is available
   */
  checkStorageAvailability() {
    try {
      const testKey = '__mad_dad_storage_test__';
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.warn('Mad Dad Labz: Local storage unavailable, Easter eggs will run session-only.');
      return false;
    }
  }
}

// Initialize the Easter egg system when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.madDadEggs = new MadDadEasterEggSystem();
  });
} else {
  window.madDadEggs = new MadDadEasterEggSystem();
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.madDadEggs) {
    window.madDadEggs.destroy();
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MadDadEasterEggSystem;
}